/* eslint no-console: 0 */

/**
 * Ownership and cleanup of releasable resources (anything with `release()`,
 * typically controller `ReleasableRef`s) under async `setup()`, where Vue
 * lifecycle APIs stop working after the first `await`. See AGENTS.md
 * "Async setup() and Composable Lifecycles".
 *
 * Component side — tie resources to unmount:
 * - {@link useReleaser}: created in setup before the first `await`; every
 *   acquisition is passed through `releaser.add(...)`.
 *
 * Composable side — composables never touch lifecycle; they hand their
 * cleanup to the caller:
 * - {@link withRelease}: attach a `release()` to a ref the composable returns.
 * - {@link withReleaseScope}: bundle several sequential acquisitions into one
 *   `ReleasableRef`, rolling back on mid-build failure.
 * - {@link acquireAll}: `Promise.all` that releases the successes when a
 *   sibling fails.
 * - {@link bindAsync}: re-derive a releasable binding when a source ref
 *   changes, releasing the previous one.
 */

import {
  computed,
  customRef,
  onBeforeUnmount,
  ref,
  watch,
  type Ref,
} from "vue";

import type { ReleasableRef } from "@ctrl/controller-rpc";

function releasableRefOfRef<T>(
  r: Ref<T>,
  release: () => void
): ReleasableRef<T> {
  const value = computed(() => r.value);
  const releaseRef = (): T => {
    release();
    return r.value;
  };
  const map = <U>(f: (v: T) => U): ReleasableRef<U> =>
    releasableRefOfRef(
      computed(() => f(r.value)),
      () => {
        releaseRef();
      }
    );
  return Object.assign(value, { release: releaseRef, map });
}

/**
 * Attach a release() method to a Vue ref and return it as a ReleasableRef.
 *
 * Useful for composing with APIs returning ReleasableRef values.
 *
 * Rule of thumb:
 * - Common case: you return a container (array/map/record) that *contains* releasable resources
 *   (often `ReleasableRef`s), and you want one `.release()` that releases everything inside.
 * - More general: you return a plain `ref(...)` / `computed(...)` from an async boundary, but that
 *   value *owns cleanup* (watchers/subscriptions/timers/child `ReleasableRef`s). `withRelease()`
 *   lets you attach that cleanup to the returned ref so callers (and helpers like `bindAsync()`)
 *   can trigger it.
 * - Not needed when the thing you return is already a `ReleasableRef` (e.g. `controller.foo.get()`):
 *   `bindAsync()` can call `.release()` on re-bind without extra wrapping.
 *
 * Concrete example:
 *
 * bindAsync() will call `.release()` on the previously-bound value when the source changes:
 *
 * - Without withRelease(): you return a plain Ref, so there is nothing for bindAsync to call.
 *   You must manually track and release the previous resources yourself.
 *
 *   ```
 *   let prev: Array<{ release: () => void }> = [];
 *   const rows = await bindAsync(tableIdRef, async (tableId) => {
 *     // manual cleanup on re-bind:
 *     prev.forEach((r) => r.release());
 *     prev = [];
 *
 *     const resources = await loadResourcesFor(tableId); // each has `.release()`
 *     prev = resources;
 *     return ref(resources); // NOTE: plain Ref => no `.release()` for bindAsync to call
 *   });
 *   // also remember to clean up `prev` on unmount / caller release
 *   ```
 *
 * - With withRelease(): you attach cleanup to the returned ref, so bindAsync can clean up
 *   the previous value automatically when it re-binds, and callers can clean up with one call.
 *
 *   ```
 *   const rows = await bindAsync(tableIdRef, async (tableId) => {
 *     const resources = await loadResourcesFor(tableId);
 *     return withRelease(ref(resources), () => {
 *       resources.forEach((r) => r.release());
 *     });
 *   });
 *   // later: rows.release() is now available and will cascade to the resources
 *   ```
 */
export function withRelease<T>(
  r: Ref<T>,
  release: () => void
): Readonly<ReleasableRef<T>> {
  return releasableRefOfRef(r, release);
}

/** Call release() and ignore errors (best-effort). */
export function releaseBestEffort(
  r: { release: () => unknown } | null | undefined
): void {
  try {
    r?.release();
  } catch {
    // best-effort
  }
}

/**
 * Build a ReleasableRef out of several owned resources.
 *
 * This is the *composable-side* counterpart of {@link useReleaser}:
 * composables cannot touch component lifecycle, so one that acquires several
 * resources uses a scope to bundle them into a single `ReleasableRef` — and
 * to roll back the ones already acquired if a later acquisition fails
 * mid-build. The calling component then registers that one ref with its
 * releaser (`releaser.add(useProfile(ctrl))`), and release cascades.
 *
 * `own()` registers a resource with the scope and returns it, so acquisitions
 * read naturally:
 *
 * ```
 * const profile = await withReleaseScope(async (own) => {
 *   const a = own(await controller.foo.get("a"));
 *   const b = own(await bindAsync(a, (v) => controller.bar.of(v)));
 *   return computed(() => ({ a: a.value, b: b.value }));
 * });
 * // later: profile.release() releases b then a, exactly once.
 * ```
 *
 * Guarantees:
 * - If `build` throws, everything owned so far is released (in reverse
 *   acquisition order) before the error propagates — no partial leaks.
 * - On success, the returned ref's `release()` releases everything owned,
 *   in reverse acquisition order, and is idempotent.
 *
 * CAUTION: do not call `own()` from concurrent branches of a `Promise.all`
 * inside `build`: when one branch rejects, siblings still in flight would
 * register resources after the rollback already ran. Use {@link acquireAll}
 * for parallel acquisitions instead.
 */
export async function withReleaseScope<T>(
  build: (
    own: <R extends { release: () => unknown }>(resource: R) => R
  ) => Promise<Ref<T>>
): Promise<Readonly<ReleasableRef<T>>> {
  const owned: { release: () => unknown }[] = [];
  const own = <R extends { release: () => unknown }>(resource: R): R => {
    owned.push(resource);
    return resource;
  };
  const releaseOwned = () => {
    [...owned].reverse().forEach(releaseBestEffort);
    owned.length = 0;
  };
  try {
    const value = await build(own);
    return withRelease(value, releaseOwned);
  } catch (error) {
    releaseOwned();
    throw error;
  }
}

/**
 * `Promise.all` for parallel acquisitions of releasable resources.
 *
 * Waits for ALL tasks to settle (unlike `Promise.all`, which rejects while
 * siblings are still in flight); if any failed, releases the successes and
 * throws the first failure. Tasks are responsible for not leaking their own
 * partial acquisitions on failure (e.g. by being {@link withReleaseScope}s).
 *
 * For composables. In a component setup, a plain `Promise.all` is already
 * safe when each entry goes through `releaser.add(...)` (the releaser holds
 * the sibling promises and releases them as they land) — see
 * {@link useReleaser}.
 */
export async function acquireAll<T extends { release: () => unknown }>(
  tasks: Promise<T>[]
): Promise<T[]> {
  const settled = await Promise.allSettled(tasks);
  const failure = settled.find(
    (result): result is PromiseRejectedResult => result.status === "rejected"
  );
  const acquired = settled.flatMap((result) =>
    result.status === "fulfilled" ? [result.value] : []
  );
  if (failure) {
    acquired.forEach(releaseBestEffort);
    throw failure.reason;
  }
  return acquired;
}

/**
 * Resolve with `true` when `ref` holds a value accepted by `ready` — however
 * long that takes, unless `timeoutMs` is given, in which case it resolves
 * with `false` after the timeout. The watcher is stopped manually, so this
 * is safe outside a component effect scope; the caller keeps ownership of
 * `ref` (for a `ReleasableRef`, releasing it stays the caller's
 * responsibility).
 */
export function waitForRefValue<T>(
  ref: Ref<T>,
  ready: (value: T) => boolean,
  timeoutMs?: number
): Promise<boolean> {
  return new Promise((resolve) => {
    let cleanup = () => {};
    const settle = (result: boolean) => {
      cleanup();
      resolve(result);
    };
    const stop = watch(
      () => ref.value,
      (value) => {
        if (ready(value)) settle(true);
      },
      { immediate: true }
    );
    const timer =
      timeoutMs === undefined
        ? undefined
        : window.setTimeout(() => settle(false), timeoutMs);
    cleanup = () => {
      stop();
      if (timer !== undefined) window.clearTimeout(timer);
    };
    if (ready(ref.value)) settle(true);
  });
}

/**
 * A `ReleasableRef` holding a plain value, with a no-op `release()`.
 *
 * Use it where an API expects a `ReleasableRef` but the value owns no
 * resources — e.g. the "empty" branch of a `bindAsync` callback:
 * `if (!id) return releasableRef(null);`.
 */
export const releasableRef = <T>(v: T): Readonly<ReleasableRef<T>> => {
  return releasableRefOfRef(ref<T>(v), () => {});
};

/**
 * Reactively re-derive a releasable binding from a source ref.
 *
 * Awaits `f(v.value)` for the initial binding, then re-runs `f` whenever `v`
 * changes, releasing the previous binding first (if it is a `ReleasableRef`)
 * — this is why `f` should return a `ReleasableRef` (via `withRelease` or
 * `releasableRef`) whenever the produced value owns resources; see
 * {@link withRelease} for the leak that results otherwise.
 *
 * The returned ref tracks the current binding's value (`undefined` while a
 * re-bind errored). Its `release()` stops the internal watchers and releases
 * the current binding; until then the source watcher keeps re-binding on
 * behalf of the consumer.
 *
 * The internal watchers are stopped manually by `release()`, not by a
 * component effect scope, so `bindAsync` is safe to call after an `await` —
 * but the caller MUST trigger `release()`: components register the call's
 * promise with their releaser (`releaser.add(bindAsync(...))`), composables
 * `own()` it in their {@link withReleaseScope} or fold it into their returned
 * `release()`.
 *
 * Re-binds are racy by nature: unlike a `computed` — which re-evaluates
 * atomically and whose stale results own nothing and can simply be dropped —
 * `f` is async and returns an owned resource, so the source may change again
 * (or `release()` may be called) while a re-bind is still suspended in `f`,
 * and every produced binding must be released exactly once. Each re-bind is
 * therefore a numbered run: it takes ownership of the live binding up front
 * (stops its watcher and releases it, unless an earlier still-suspended run
 * already did), and when its `f` settles it may only install the result if it
 * is still the newest run. A superseded run releases the binding its `f` just
 * produced instead of installing it. This keeps exactly one binding live at
 * all times: no double release when re-binds overlap, no orphaned binding
 * landing after a newer run or after `release()`. Runs are scoped to one
 * `bindAsync()` call: using the same source ref in several bindings is fine,
 * each re-binds independently when it changes.
 */
export async function bindAsync<T, U>(
  v: Ref<T>,
  f: (value: T) => Promise<ReleasableRef<U>>
): Promise<Readonly<ReleasableRef<U | undefined>>>;

export async function bindAsync<T, U>(
  v: Ref<T>,
  f: (value: T) => Promise<Ref<U>>
): Promise<Readonly<Ref<U | undefined>>>;

export async function bindAsync<T, U>(
  v: Ref<T>,
  f: (value: T) => Promise<Ref<U>>
): Promise<Readonly<Ref<U | undefined>>> {
  let bound = await f(v.value);
  let unwatchSource: (() => void) | null = null;
  // Doubles as the "is `bound` live?" marker: null while a re-bind is
  // suspended in `f` (the old binding is already released, the new one not
  // installed yet) and after `release()`.
  let unwatchBound: (() => void) | null = null;
  // Numbers the re-runs of THIS binding (each `bindAsync()` call has its own
  // counter — a ref used as the source of several bindings is fine, they are
  // independent). Bumped by every re-bind and by `release()`: a re-bind that
  // awakes from `await f(...)` with a stale token was superseded by a newer
  // re-run of this same binding (or by `release()`), so it must release the
  // binding `f` just produced instead of installing it.
  let bindRun = 0;

  const res = customRef<U | undefined>((track, trigger) => {
    unwatchBound = watch(bound, () => trigger());
    unwatchSource = watch(v, async (value) => {
      const thisRun = ++bindRun;
      try {
        if (unwatchBound) {
          unwatchBound();
          unwatchBound = null;
          releaseRefIfPresent(bound);
        }
        const next = await f(value);
        if (thisRun !== bindRun) {
          releaseRefIfPresent(next);
          return;
        }
        bound = next;
        unwatchBound = watch(bound, () => trigger());
        trigger();
      } catch (error) {
        console.warn("Error in bindAsync watcher:", error);
      }
    });
    return {
      get() {
        track();
        try {
          return bound.value;
        } catch (error) {
          console.warn("Error accessing bound value:", error);
          return undefined;
        }
      },
      set(_v: U | undefined) {
        throw Error("unreachable");
      },
    };
  });

  const resRelease = () => {
    // Invalidate any re-bind still suspended in `f`: when it lands it will
    // release its own result instead of installing it.
    bindRun++;
    try {
      if (unwatchSource) {
        unwatchSource();
        unwatchSource = null;
      }
      // Release the live binding — unless a suspended re-bind already
      // released it and took ownership (unwatchBound is null then).
      if (unwatchBound) {
        unwatchBound();
        unwatchBound = null;
        releaseRefIfPresent(bound);
      }
    } catch (error) {
      console.warn("Error releasing ref:", error);
    }
  };
  return releasableRefOfRef(res, resRelease);
}

const releaseRefIfPresent = (value: Ref<unknown>): void => {
  const release: unknown = Reflect.get(value, "release");
  if (typeof release === "function") Reflect.apply(release, value, []);
};

/** Any object with a release() method */
type Releasable = { release: () => unknown };

type MaybePromise<T> = T | Promise<T>;

export type Releaser = {
  /**
   * Register a resource (or a promise of one) to be released on unmount,
   * and return it unchanged so acquisitions read naturally:
   *
   * ```
   * const agent = await releaser.add(useAgent(...));
   * ```
   */
  add<V extends MaybePromise<Releasable | ReleasableRef<unknown>>>(
    resource: V
  ): V;
  /**
   * Defer a plain cleanup function to run on unmount (before the DOM is
   * torn down). Sugar for `add({ release: cleanup })` — for cleanups that
   * are not tied to a releasable resource, typically ones born in
   * `onMounted` (observers, listeners, debounced callbacks):
   *
   * ```
   * onMounted(() => {
   *   const observer = new IntersectionObserver(...);
   *   releaser.defer(() => observer.disconnect());
   * });
   * ```
   */
  defer(cleanup: () => void): void;
};

/**
 * The on-unmount cleanup collector for resources acquired in an async
 * `setup()`.
 *
 * Create it **before the first `await`** (it registers an `onBeforeUnmount`
 * hook — enforced by the `routine/no-lifecycle-outside-setup` ESLint rule),
 * then `add()` every releasable resource, anywhere in setup:
 *
 * ```
 * const releaser = useReleaser();
 *
 * // Async acquisition: add the promise itself, then await it.
 * const agent = await releaser.add(useAgent(...));
 *
 * // Parallel acquisitions:
 * const [a, b] = await Promise.all([
 *   releaser.add(controller.foo.get("a")),
 *   releaser.add(controller.bar.get("b")),
 * ]);
 *
 * // Sync acquisition — fine even after an `await`:
 * const conversations = releaser.add(useConversations(...));
 *
 * // Plain cleanup (no resource object), e.g. from inside onMounted:
 * releaser.defer(() => observer.disconnect());
 * ```
 *
 * Guarantees:
 * - Adding an acquisition *promise* (rather than its awaited result) releases
 *   the resource even when setup never resumes past the `await` — a sibling
 *   in a `Promise.all` rejects, setup throws later, or the component unmounts
 *   while setup is still suspended: unmount does `promise.then(release)`,
 *   which fires whenever the resource lands.
 * - Adding *after* unmount (async setup keeps running when a suspended
 *   component is torn down) releases the resource as soon as it is available.
 * - Rejected promises are ignored: a failed acquisition never produced a
 *   resource, and the rejection itself is setup's to handle.
 *
 * This helper is for components. Composables must not call it (they have no
 * reliable lifecycle to attach to — see AGENTS.md); they return a `release()`
 * function or a `ReleasableRef` that the component `add()`s, built with
 * {@link withRelease}, {@link withReleaseScope}, or {@link acquireAll}.
 */
export function useReleaser(): Releaser {
  let unmounted = false;
  const pending: Promise<Releasable | ReleasableRef<unknown>>[] = [];
  const releaseWhenAvailable = (
    p: Promise<Releasable | ReleasableRef<unknown>>
  ) =>
    p.then(releaseBestEffort, () => {
      // A rejected acquisition never produced a resource: nothing to release.
    });
  onBeforeUnmount(() => {
    unmounted = true;
    pending.forEach(releaseWhenAvailable);
    pending.length = 0;
  });
  const add = <V extends MaybePromise<Releasable | ReleasableRef<unknown>>>(
    resource: V
  ): V => {
    const p = Promise.resolve<Releasable | ReleasableRef<unknown>>(resource);
    if (unmounted) releaseWhenAvailable(p);
    else pending.push(p);
    return resource;
  };
  return {
    add,
    defer(cleanup) {
      add({ release: cleanup });
    },
  };
}
